import React, { useReducer, useContext, useEffect, useRef, useCallback } from "react";
import { endpoints } from "../utils/Constants";
import { useLocalStorage, userTokens } from "../utils/Hooks/useLocalStorage";
import { getLoginContext } from "./Auth";
import { fetch, fetchJSONWithToken } from "./fetchRefresh";

// set up initial state which is used in the below `AuthProvider` function
const initialContext = {
    username: "",
    email: "",
    role: "",
    refresh: "",
    jwt: null,
};

interface ExtraDetails {
    supplierId: string;
    clientId: string;
    clientDepartmentId: string;
    clientStaffId: string;
}

export type authDetails = typeof initialContext & Partial<ExtraDetails> & Record<string, unknown>;

// create the context
const AuthStateContext = React.createContext<
    [
        authDetails,
        React.Dispatch<{
            payload?: authDetails;
            type: "setAuthDetails" | "removeAuthDetails";
        }>
    ]
>(undefined as any);

// set up the reducer - same as Redux, allows us to process more complex changes
// to the state within the context API
const reducer = (
    _,
    action: { payload?: any; type: "setAuthDetails" | "removeAuthDetails" }
) => {
    switch (action.type) {
        case "setAuthDetails":
            if (!action.payload) return initialContext;
            return {
                username: action.payload.username,
                email: action.payload.email,
                refresh: action.payload.refresh,
                role: action.payload.role,
                jwt: action.payload.jwt,
                adminId: action.payload.adminId,
                clientDepartmentId: action.payload.clientDepartmentId,
                clientId: action.payload.clientId,
                clientStaffId: action.payload.clientStaffId,
                supplierId: action.payload.supplierId,
                canViewSupplierInfo: action.payload.canViewSupplierInfo,
                terms: action.payload.terms,
                qGov: action.payload.qGov,
                managesMessages: action.payload.managesMessages,
                managesWorkflow: action.payload.managesWorkflow,
                serverVersion: action.payload.serverVersion,
            };
        case "removeAuthDetails":
            return initialContext;

        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
};


// create and export the AuthProvider - this is imported to the _app.js file
// and wrapped around the whole app, providing context to the whole app, and
// is called each time this specific context is accessed (updated or retrieved)

const AuthProvider = ({ children }) => {
    const [user, dispatchTokens] = useLocalStorage<userTokens>("user");
    const [state, dispatch] = useReducer(reducer, initialContext);
    const initialised = useRef<NodeJS.Timeout | true>()
    const tested = useRef(false);
    const renewToken = useCallback(async () => {
        try {
            const { body } = (await fetchJSONWithToken("/auth/renewaccess")) as {
                body: userTokens;
                response: Response;
            };
            // Remove refreshed status - a renewal, unlike a refresh, triggers the next renewal automatically.
            const { refreshed, ...newUser } = { refreshTokens: [], ...user, accessToken: body.accessToken };
            // Clear any possibly pending renewals
            if (initialised.current && initialised.current !== true) clearTimeout(initialised.current);
            // Schedule next renewal 3/4 into the token's span
            initialised.current = setTimeout(renewToken, (newUser.accessToken.expiresIn * 0.75) * 1000);
            dispatchTokens(newUser);
            // If the renewal failed, stop trying. The client will refresh the token later.
        } catch {
            if (initialised.current && initialised.current !== true) clearTimeout(initialised.current);
            initialised.current = undefined;
        }
    }, [dispatchTokens, user]);
    useEffect(() => {
        if (user?.refreshed) {
            // If a token had just been refreshed
            if (initialised.current && initialised.current !== true)
                // Clear the pending renewal (if set)
                clearTimeout(initialised.current);
            // Schedule a new one
            initialised.current = setTimeout(renewToken, (user.accessToken.expiresIn - 0.75) * 1000);
            // Count the token as refreshed
            dispatchTokens({ ...user, refreshed: undefined });
        }
        // If there's no pending renewal
        else if (user && !initialised.current) {
            // Cause one immediately (The token might not be new, meaning that it might expire soon).
            renewToken();
        }
        (async () => {
            if (tested.current) return;
            else {
                tested.current = true;
                const version = await fetch(endpoints.apiVersion).catch(e => e);
                if (version.status && version.status === 401) {
                    dispatch({
                        type: "removeAuthDetails",
                    });
                }
                else {
                    const payload = getLoginContext(user);
                    dispatch({
                        type: "setAuthDetails",
                        payload: {
                            ...payload,
                            applicationVersion: (version.body as any).applicationVersion,
                        },
                    });
                }
            }
        })();
    }, [user, renewToken, dispatchTokens]);
    return (
        <AuthStateContext.Provider value={[state, dispatch]}>
            {children}
        </AuthStateContext.Provider>
    );
};

// useContext hook - export here to keep code for global auth state
// together in this file, allowing user info to be accessed and updated
// in any functional component using the hook
const useAuth = () => useContext(AuthStateContext);

export { useAuth, AuthProvider, AuthStateContext };
